import { Box, Card, CardActionArea, CardContent, Chip, Grid, Typography } from '@mui/material';
import React from 'react';

interface SSRService {
  id: string;
  name: string;
  brand_name?: string;
  is_select?: boolean;
  category?: string;
  description?: string;
  base_price?: number; // cents
  price_range_min?: number; // cents
  price_range_max?: number; // cents
  // PR-9 tile rail. When tile_unit_basis !== 'flat', render a
  // "From $X per <unit>" rail instead of the price range. Services that
  // scale with a natural unit (phone lines, seats, users, sites) set
  // these via the reshape/seed pipeline; flat-priced services leave
  // tile_unit_basis='flat' and keep today's behavior.
  tile_unit_basis?: 'flat' | 'per_line' | 'per_user' | 'per_seat' | 'per_site';
  tile_price_cents?: number | null;
  tile_unit_label?: string | null;
}

interface ServiceGridSSRProps {
  services: SSRService[];
  catalogInstance?: string; // if set, links go to /c/[catalogInstance]/services/[id]
}

const SelectBadge = () => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px', border: '1px solid #D4AF37', bgcolor: 'rgba(212, 175, 55, 0.12)', color: '#8c6a00', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', width: 'fit-content' }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.5L14.95 8.48L21.55 9.44L16.78 14.08L17.9 20.64L12 17.54L6.1 20.64L7.22 14.08L2.45 9.44L9.05 8.48L12 2.5Z" fill="#D4AF37" />
    </svg>
    Select
  </Box>
);

function formatPrice(svc: SSRService): string {
  // PR-9: per-unit rail wins whenever the row opts into it. We render two
  // decimal places on the rail because unit pricing is usually meaningful
  // below the dollar ($39.95/line, $0.85/user, etc.) — the range fallback
  // keeps the legacy integer-dollar format so nothing else regresses.
  if (
    svc.tile_unit_basis &&
    svc.tile_unit_basis !== 'flat' &&
    typeof svc.tile_price_cents === 'number' &&
    svc.tile_unit_label
  ) {
    const dollars = (svc.tile_price_cents / 100).toFixed(2);
    return `From $${dollars} ${svc.tile_unit_label}`;
  }

  const toMonthly = (cents: number) => `$${(cents / 100).toFixed(0)}/mo`;
  const { base_price: base, price_range_min: min, price_range_max: max } = svc;
  if (min && max && max > min) return `${toMonthly(min)} – ${toMonthly(max)}`;
  if (base && base > 0) return `from ${toMonthly(base)}`;
  if (min && min > 0) return `from ${toMonthly(min)}`;
  return '';
}

export function ServiceGridSSR({ services, catalogInstance }: ServiceGridSSRProps) {
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={3}>
        {services.map((svc) => {
          const href = catalogInstance ? `/c/${catalogInstance}/services/${svc.id}` : `/services/${svc.id}`;
          const price = formatPrice(svc);
          const desc = svc.description
            ? svc.description.slice(0, 120) + (svc.description.length > 120 ? '…' : '')
            : '';

          return (
            <Grid item xs={12} sm={6} md={4} key={svc.id}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea component="a" href={href} sx={{ flexGrow: 1 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {svc.category && (
                      <Chip label={svc.category} size="small" sx={{ alignSelf: 'flex-start' }} />
                    )}
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {svc.name}
                    </Typography>
                    {svc.brand_name && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {svc.is_select === true && <SelectBadge />}
                        <Typography variant="caption" color="text.secondary">
                          by {svc.brand_name}
                        </Typography>
                      </Box>
                    )}
                    {desc && (
                      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {desc}
                      </Typography>
                    )}
                    {price && (
                      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mt: 'auto' }}>
                        {price}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
